import { useEffect, useMemo, useState } from 'react'
import { FormFieldData, FormFieldType } from './fields/type'
import { FormFieldRenderer } from './renderer/FormFieldRenderer'
import clsx from 'clsx'
import { DetailSubFormHeader } from './DetailSubFormHeader'
import { useFormContext } from 'react-hook-form'
import { useDetailForm } from './FormProviderV2'

type Props<T> = {
  title?: string
  /**
   * Form 의 Object type인 property 편집을 위한 name
   */
  name?: string
  fields: FormFieldData<T>[]
  /**
   * Type inference를 위한 data
   */
  data?: T
  className?: string
}

export const DetailForm = <T,>({
  title,
  name,
  fields = [],
  className,
}: Props<T>) => {
  const context = useDetailForm()
  const form = useFormContext()

  const [isLoading, setIsLoading] = useState(true)

  const fieldsMemo = useMemo(() => {
    if (name) {
      return fields.map((field) => {
        if (field.type === FormFieldType.FIELD_GROUP_READ_ONLY) {
          return {
            ...field,
            fields: field.fields.map((field) => ({
              ...field,
              key: `${name}.${field.key}`,
            })),
          }
        }

        return {
          ...field,
          key: `${name}.${field.key}`,
        }
      })
    }

    return fields
  }, [fields, name])

  useEffect(() => {
    if (!isLoading) {
      return
    }

    for (const field of fieldsMemo) {
      if (typeof field.default !== 'undefined') {
        form.setValue(field.key, field.default)
      }
    }

    setIsLoading(false)
  }, [fieldsMemo, form, isLoading])

  context.setFieldGroup(name || '@origin', fieldsMemo)

  return (
    <section className={clsx('space-y-1', className)}>
      {title && <DetailSubFormHeader title={title} />}
      {!isLoading && (
        <div className="divide-y divide-slate-200 overflow-hidden  border">
          {fieldsMemo.map((fieldData) => {
            return (
              <FormFieldRenderer renderType="detail" fieldData={fieldData} />
            )
          })}
        </div>
      )}
    </section>
  )
}
